import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, UserPlus } from 'lucide-react';
import AllergyInput from '@/components/ui/allergy-input';
import AllergyBadge from '@/components/ui/allergy-badge';
import { patientsApi } from '../../../services/patientsService';
import { useSelector } from 'react-redux';
import { selectCurrentClinicId } from '../../../store/slices/authSlice';
import type { CreatePatientRequest, Medication } from '../../../types/api';
import { 
  validateForm, 
  formatPhoneNumber, 
  formatZipCode, 
  formatState,
  clearValidationError,
  hasValidationErrors,
  type ValidationErrors 
} from '../../../utils/patientValidation';

interface FormData {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: string;
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  address_street: string;
  address_city: string;
  address_state: string;
  address_postalCode: string;
}


interface CreatePatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreatePatientRequest) => Promise<{ id: string; href: string }>; // Returns patient info
  loading?: boolean;
}

const CreatePatientModal: React.FC<CreatePatientModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  loading = false
}) => {
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    date_of_birth: '',
    gender: 'male' as const,
    address_street: '',
    address_city: '',
    address_state: '',
    address_postalCode: ''
  });

  const [selectedAllergies, setSelectedAllergies] = useState<Medication[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allergySaveError, setAllergySaveError] = useState<string | null>(null);
  
  const currentClinicId = useSelector(selectCurrentClinicId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form data using shared utility
    const validationErrors = validateForm(formData, false); // false = isCreate mode
    setErrors(validationErrors);

    // If there are validation errors, don't submit
    if (hasValidationErrors(validationErrors)) {
      setIsSubmitting(false);
      return;
    }

    const createData: CreatePatientRequest = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      // clinic_id removed - provided via HATEOAS URL path
      date_of_birth: formData.date_of_birth,
      gender: formData.gender,
      address: {
        street: [formData.address_street.trim()],
        city: formData.address_city.trim(),
        state: formData.address_state.trim().toUpperCase(),
        postal_code: formData.address_postalCode.trim()
      }
    };

    try {
      // Create patient first
      const patientResult = await onCreate(createData);
      
      // If there are allergies, add them after patient creation
      if (selectedAllergies.length > 0 && currentClinicId) {
        try {
          const allergyIds = selectedAllergies.map(allergy => allergy.id);
          await patientsApi.addPatientAllergies(currentClinicId, patientResult.id, allergyIds);
        } catch (allergyError) {
          console.error('Failed to add allergies:', allergyError);
          setAllergySaveError('Patient created but failed to save allergies');
          // Don't return here - still want to reset form and close
        }
      }
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        date_of_birth: '',
        gender: 'male',
        address_street: '',
        address_city: '',
        address_state: '',
        address_postalCode: ''
      });
      setSelectedAllergies([]);
      setErrors({});
      setAllergySaveError(null);
      onClose();
    } catch (error) {
      console.error('Failed to create patient:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => clearValidationError(prev, field as keyof ValidationErrors));
    }
  };

  // Specialized handler for phone number with real-time formatting
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  // Specialized handler for state with auto-uppercase
  const handleStateChange = (value: string) => {
    const formatted = formatState(value);
    handleInputChange('address_state', formatted);
  };

  const handleZipCodeChange = (value: string) => {
    const formatted = formatZipCode(value);
    handleInputChange('address_postalCode', formatted);
  };

  const handleAddAllergy = (medication: Medication) => {
    if (!selectedAllergies.find(allergy => allergy.id === medication.id)) {
      setSelectedAllergies(prev => [...prev, medication]);
    }
  };

  const handleRemoveAllergy = (medicationId: string) => {
    setSelectedAllergies(prev => prev.filter(allergy => allergy.id !== medicationId));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-syne font-medium text-vitl-navy flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
              <UserPlus className="w-6 h-6" />
            </div>
            <div>
              <div>Add New Patient</div>
              <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                Create a new patient record for the clinic
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                  First Name *
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('first_name', e.target.value)}
                  className={`mt-1 ${errors.first_name ? 'border-red-500' : ''}`}
                  placeholder="First name"
                  required
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('last_name', e.target.value)}
                  className={`mt-1 ${errors.last_name ? 'border-red-500' : ''}`}
                  placeholder="Last name"
                  required
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePhoneChange(e.target.value)}
                  className={`mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                  required
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  className={`mt-1 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="patient@example.com"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dob" className="text-sm font-medium text-gray-700">
                  Date of Birth *
                </Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('date_of_birth', e.target.value)}
                  className={`mt-1 ${errors.date_of_birth ? 'border-red-500' : ''}`}
                  required
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>
                )}
              </div>
              <div>
                <Label htmlFor="gender" className="text-sm font-medium text-gray-700">
                  Gender *
                </Label>
                <select 
                  id="gender"
                  value={formData.gender}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('gender', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vitl-green"
                  required
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Address Information</h3>
            
            <div>
              <Label htmlFor="address_lines" className="text-sm font-medium text-gray-700">
                Street Address *
              </Label>
              <Input
                id="address_lines"
                value={formData.address_street}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('address_street', e.target.value)}
                className={`mt-1 ${errors.address_street ? 'border-red-500' : ''}`}
                placeholder="123 Main Street, Apt 4B"
                required
              />
              {errors.address_street && (
                <p className="mt-1 text-sm text-red-600">{errors.address_street}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="address_city" className="text-sm font-medium text-gray-700">
                  City *
                </Label>
                <Input
                  id="address_city"
                  value={formData.address_city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('address_city', e.target.value)}
                  className={`mt-1 ${errors.address_city ? 'border-red-500' : ''}`}
                  placeholder="New York"
                  required
                />
                {errors.address_city && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_city}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address_state" className="text-sm font-medium text-gray-700">
                  State *
                </Label>
                <Input
                  id="address_state"
                  value={formData.address_state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleStateChange(e.target.value)}
                  className={`mt-1 ${errors.address_state ? 'border-red-500' : ''}`}
                  placeholder="NY"
                  maxLength={2}
                  required
                />
                {errors.address_state && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_state}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address_postal_code" className="text-sm font-medium text-gray-700">
                  Zip Code *
                </Label>
                <Input
                  id="address_postal_code"
                  value={formData.address_postalCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleZipCodeChange(e.target.value)}
                  className={`mt-1 ${errors.address_postalCode ? 'border-red-500' : ''}`}
                  placeholder="12345 or 12345-6789"
                  maxLength={10}
                  required
                />
                {errors.address_postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_postalCode}</p>
                )}
              </div>
            </div>
          </div>

          {/* Allergies Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Allergies</h3>
            <p className="text-sm text-gray-600">
              Note: Allergies will be saved after patient creation
            </p>
            
            {allergySaveError && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-700 text-sm">⚠️ {allergySaveError}</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Add Medication Allergies
              </Label>
              <div className="mt-2">
                <AllergyInput
                  onAdd={handleAddAllergy}
                  placeholder="Search for medications..."
                  disabled={loading || isSubmitting}
                />
              </div>
            </div>

            {selectedAllergies.length > 0 ? (
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Allergies to Add
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedAllergies.map((medication) => (
                    <AllergyBadge
                      key={medication.id}
                      medication={medication.name}
                      onRemove={() => handleRemoveAllergy(medication.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">No allergies selected</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-emerald-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white"
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? 'Creating...' : 'Create Patient'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePatientModal;